import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Shield, Eye, EyeOff, Save, Info } from "lucide-react";
import { toast } from "sonner";

interface ClientConsentManagerProps {
  clinicianId: string;
}

interface ConsentSettings {
  share_mood_data: boolean;
  share_check_ins: boolean;
  share_sleep_data: boolean;
  share_journal_entries: boolean;
  share_goals_progress: boolean;
  share_assessments: boolean;
  share_crisis_events: boolean;
  share_ai_summaries: boolean;
  share_nutrition_data: boolean;
  share_exercise_data: boolean;
  share_therapy_sessions: boolean;
}

export function ClientConsentManager({ clinicianId }: ClientConsentManagerProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [hasExistingConsent, setHasExistingConsent] = useState(false);

  const [settings, setSettings] = useState<ConsentSettings>({
    share_mood_data: false,
    share_check_ins: false,
    share_sleep_data: false,
    share_journal_entries: false,
    share_goals_progress: false,
    share_assessments: false,
    share_crisis_events: false,
    share_ai_summaries: false,
    share_nutrition_data: false,
    share_exercise_data: false,
    share_therapy_sessions: false
  });

  useEffect(() => {
    loadConsent();
  }, [clinicianId]);

  const loadConsent = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setLoading(false);
      return;
    }
    setUserId(user.id);

    const { data } = await supabase
      .from('client_data_consent')
      .select('*')
      .eq('client_id', user.id)
      .eq('clinician_id', clinicianId)
      .maybeSingle();

    if (data) {
      setHasExistingConsent(true);
      setSettings({
        share_mood_data: data.share_mood_data,
        share_check_ins: data.share_check_ins,
        share_sleep_data: data.share_sleep_data,
        share_journal_entries: data.share_journal_entries,
        share_goals_progress: data.share_goals_progress,
        share_assessments: data.share_assessments,
        share_crisis_events: data.share_crisis_events,
        share_ai_summaries: data.share_ai_summaries,
        share_nutrition_data: data.share_nutrition_data,
        share_exercise_data: data.share_exercise_data,
        share_therapy_sessions: data.share_therapy_sessions
      });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!userId) return;

    setSaving(true);
    const data = {
      client_id: userId,
      clinician_id: clinicianId,
      ...settings,
      last_updated_at: new Date().toISOString()
    };

    let error;
    if (hasExistingConsent) {
      const result = await supabase
        .from('client_data_consent')
        .update(data)
        .eq('client_id', userId)
        .eq('clinician_id', clinicianId);
      error = result.error;
    } else {
      const result = await supabase
        .from('client_data_consent')
        .insert(data);
      error = result.error;
      if (!error) setHasExistingConsent(true);
    }

    if (error) {
      console.error('Error saving consent:', error);
      toast.error('Failed to save consent settings');
    } else {
      toast.success('Consent settings saved');
    }
    setSaving(false);
  };

  const toggleAll = (enabled: boolean) => {
    setSettings({
      share_mood_data: enabled,
      share_check_ins: enabled,
      share_sleep_data: enabled,
      share_journal_entries: enabled,
      share_goals_progress: enabled,
      share_assessments: enabled,
      share_crisis_events: enabled,
      share_ai_summaries: enabled,
      share_nutrition_data: enabled,
      share_exercise_data: enabled,
      share_therapy_sessions: enabled
    });
  };

  if (loading) {
    return (
      <div className="flex items-centre justify-centre p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const consentOptions = [
    { key: 'share_mood_data', label: 'Mood & Emotions', description: 'Daily mood check-ins and emotional states' },
    { key: 'share_check_ins', label: 'Daily Check-ins', description: 'Full daily check-in responses including body, mind, spirit' },
    { key: 'share_sleep_data', label: 'Sleep Data', description: 'Sleep duration, quality, and patterns' },
    { key: 'share_journal_entries', label: 'Journal Entries', description: 'Written journal entries and reflections' },
    { key: 'share_goals_progress', label: 'Goals & Progress', description: 'Personal goals and completion status' },
    { key: 'share_assessments', label: 'Assessments', description: 'Completed psychological assessments' },
    { key: 'share_crisis_events', label: 'Crisis Events', description: 'Recorded crisis moments and coping actions taken' },
    { key: 'share_ai_summaries', label: 'AI Insights', description: 'AI-generated summaries and correlations' },
    { key: 'share_exercise_data', label: 'Exercise Data', description: 'Physical activity and workout logs' },
    { key: 'share_nutrition_data', label: 'Nutrition Data', description: 'Food and hydration tracking' },
    { key: 'share_therapy_sessions', label: 'AI Reflection Sessions', description: 'Conversations with the AI companion' }
  ];

  return (
    <Card className="bg-card/50 border-border/30">
      <CardHeader>
        <CardTitle className="flex items-centre gap-2 text-foreground">
          <Shield className="h-5 w-5 text-primary" />
          Data Sharing Consent
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          Control what information your practitioner can see about your progress
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="p-4 bg-muted/20 rounded-lg flex items-start gap-3 border border-border/30">
          <Info className="h-5 w-5 text-primary mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Your privacy matters</p>
            <p>
              Sharing data with your practitioner helps them provide better, more personalised support.
              You can change these settings at any time.
            </p>
          </div>
        </div>

        <div className="flex items-centre justify-between">
          <span className="font-medium text-foreground">Quick Actions</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => toggleAll(true)} className="gap-2 border-border text-foreground hover:bg-primary/10">
              <Eye className="h-4 w-4" />
              Share All
            </Button>
            <Button variant="outline" size="sm" onClick={() => toggleAll(false)} className="gap-2 border-border text-foreground hover:bg-primary/10">
              <EyeOff className="h-4 w-4" />
              Hide All
            </Button>
          </div>
        </div>

        <Separator className="bg-border/30" />

        <div className="space-y-4">
          {consentOptions.map(option => (
            <div key={option.key} className="flex items-centre justify-between">
              <div className="space-y-0.5">
                <Label htmlFor={option.key} className="font-medium cursor-pointer text-foreground">
                  {option.label}
                </Label>
                <p className="text-xs text-muted-foreground">{option.description}</p>
              </div>
              <Switch
                id={option.key}
                checked={settings[option.key as keyof ConsentSettings]}
                onCheckedChange={(checked) => 
                  setSettings(s => ({ ...s, [option.key]: checked }))
                }
                className="data-[state=checked]:bg-primary"
              />
            </div>
          ))}
        </div>

        <Separator className="bg-border/30" />

        <Button onClick={handleSave} disabled={saving} className="w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save Consent Settings'}
        </Button>
      </CardContent>
    </Card>
  );
}
